<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { XMarkIcon, MapPinIcon, DevicePhoneMobileIcon, EnvelopeOpenIcon, ArrowRightIcon, FolderIcon } from '@heroicons/vue/24/outline'
import { ref, computed } from 'vue'
import ServiceSwiper from '@/Pages/User/Services/Partials/ServiceSwiper.vue'
import Modal from '@/Components/Modal.vue'

const props = defineProps({
    service: {
        type: Object,
        required: true,
    },
})

const open_modal = ref(false)

const youtubeReels = computed(() => {
    let youtube = props.service.relationships.social_links.filter((link) => link.social_media === 'youtube-reels')

    if (!youtube || youtube.length < 1) {
        return null
    }

    return youtube[0]
})
</script>

<template>
    <AppLayout
        :title="service.name"
        :description="service.meta_description"
    >
        <template #content>
            <section class="container mx-auto pt-16 lg:pt-48 xl:px-4 2xl:px-0">
                <div class="grid grid-cols-1 gap-5 xl:grid-cols-12">
                    <div class="col-span-12 px-2 lg:px-0">
                        <h1 class="mt-5 text-left font-caveat text-5xl font-semibold text-gray-800 xl:text-6xl">
                            {{ service.name }}
                        </h1>
                    </div>

                    <div class="col-span-12 space-y-5 xl:col-span-8 xl:rounded-xl">
                        <div>
                            <ServiceSwiper :images="service.relationships.images" />
                        </div>

                        <div class="rounded-xl bg-gray-100 p-3">
                            <div
                                class="content-editor"
                                v-html="service.content"
                            />
                        </div>

                        <div class="grid grid-cols-1 gap-2 xl:grid-cols-2">
                            <div
                                v-if="service.detail_content"
                                class="space-y-5 rounded-xl bg-gray-100 p-3"
                            >
                                <h2 class="text-xl font-semibold text-gray-800">Detaily</h2>

                                <div
                                    class="content-editor"
                                    v-html="service.detail_content"
                                />
                            </div>

                            <div
                                v-if="service.how_to_reach_us"
                                class="space-y-5 rounded-xl bg-gray-100 p-3"
                            >
                                <h2 class="text-xl font-semibold text-gray-800">Jak se k nám dostanete</h2>

                                <div
                                    class="content-editor"
                                    v-html="service.how_to_reach_us"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="col-span-12 space-y-5 xl:col-span-4 xl:rounded-xl">
                        <div
                            v-if="youtubeReels"
                            class="col-span-12 flex h-[18rem] flex-col md:h-[27em] lg:h-[36rem] xl:col-span-4 xl:h-[40rem]"
                        >
                            <iframe
                                :src="youtubeReels.link"
                                title="YouTube video player"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen
                                class="h-full w-full xl:rounded-xl"
                            />
                        </div>

                        <div
                            v-if="service.google_map_location"
                            class="hidden xl:block"
                            :class="{
                                'pt-0': !youtubeReels,
                                'pt-4': youtubeReels,
                            }"
                        >
                            <iframe
                                :src="service.google_map_location"
                                class="h-[26.3rem] w-full xl:rounded-xl"
                                style="border: 0"
                                loading="lazy"
                                referrerpolicy="no-referrer-when-downgrade"
                            />
                        </div>

                        <!-- Services socials -->
                        <div
                            v-if="service.relationships?.social_links[0]"
                            class="px-2 2xl:px-0"
                        >
                            <ul class="flex">
                                <li
                                    v-for="social_link in service.relationships.social_links"
                                    :key="social_link.id"
                                >
                                    <a
                                        v-if="social_link.social_media != 'youtube-reels'"
                                        :href="social_link.link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="faster-animation relative mr-2 flex items-center space-x-4 rounded-lg border border-transparent bg-gray-50 p-2 hover:border-gray-100 hover:bg-white"
                                        :aria-label="'Odkaz na ' + social_link.social_media"
                                    >
                                        <img
                                            :src="'/src/socials/' + social_link.social_media + '.webp'"
                                            class="size-6"
                                            :alt="social_link.social_media"
                                        />

                                        <span
                                            v-if="social_link.social_media === 'web'"
                                            class="uppercase"
                                        >
                                            Web
                                        </span>
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <!-- Business Informations -->
                        <ul class="space-y-2">
                            <li
                                v-if="service.address"
                                class="flex items-center space-x-4"
                            >
                                <span class="inline-block rounded-md bg-red-100 p-2 text-red-500">
                                    <MapPinIcon class="h-6 w-6" />
                                </span>

                                <h2 class="text-sm font-semibold capitalize text-gray-700">
                                    {{ service.address }}
                                </h2>
                            </li>

                            <li
                                v-if="service.mobile_number"
                                class="flex items-center space-x-4"
                            >
                                <a
                                    class="flex items-center space-x-4"
                                    aria-label="Zavolat na telefonní číslo"
                                    :href="'tel:' + service.mobile_number"
                                >
                                    <span class="inline-block rounded-md bg-red-100 p-2 text-red-500">
                                        <DevicePhoneMobileIcon class="h-6 w-6" />
                                    </span>

                                    <h2 class="text-sm font-semibold capitalize text-gray-700">
                                        {{ service.mobile_number }}
                                    </h2>
                                </a>
                            </li>

                            <li
                                v-if="service.email"
                                class="flex items-center space-x-4"
                            >
                                <a
                                    class="flex items-center space-x-4"
                                    aria-label="Napsat na email"
                                    :href="'mailto:' + service.email"
                                >
                                    <span class="inline-block rounded-md bg-red-100 p-2 text-red-500">
                                        <EnvelopeOpenIcon class="h-6 w-6" />
                                    </span>

                                    <h2 class="text-sm font-semibold text-gray-700">
                                        {{ service.email }}
                                    </h2>
                                </a>
                            </li>
                        </ul>

                        <div v-if="service.opening_info">
                            <div class="flex">
                                <div class="w-full space-y-5 rounded-xl bg-gray-100 px-3 pt-1">
                                    <div
                                        class="content-editor"
                                        v-html="service.opening_info"
                                    />
                                </div>
                            </div>
                        </div>

                        <div v-if="service.attachment">
                            <div class="flex">
                                <div class="slower-animation w-full rounded-md border border-gray-200 bg-gray-50 hover:border-red-300">
                                    <button
                                        aria-label="Zobrazit všechny podniky"
                                        class="group inline-flex w-full items-center py-2 pl-4 pr-8 text-base font-semibold text-red-400"
                                        @click="open_modal = true"
                                    >
                                        Ceník

                                        <ArrowRightIcon class="faster-animation relative left-2 h-5 w-5 group-hover:left-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div v-if="service.relationships.blogs[0]">
                            <div class="flex">
                                <div class="slower-animation w-full rounded-md border border-gray-200 bg-gray-50 hover:border-red-300">
                                    <Link
                                        v-for="blog in service.relationships.blogs"
                                        :key="blog.id"
                                        aria-label="Zobrazit všechny podniky"
                                        :href="route('blogs.show', blog.slug)"
                                        class="group inline-flex w-full items-center py-2 pl-4 pr-8 text-base font-semibold text-red-400"
                                    >
                                        Blogy

                                        <ArrowRightIcon class="faster-animation relative left-2 h-5 w-5 group-hover:left-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Modal
                :show="open_modal"
                @close="open_modal = false"
                maxWidth="4xl"
            >
                <div class="absolute right-2 top-2">
                    <button
                        class="faster-animation flex items-center justify-center rounded-lg bg-red-500 p-2 hover:bg-red-400"
                        @click="open_modal = false"
                    >
                        <XMarkIcon class="h-7 w-7 text-white" />
                    </button>
                </div>

                <div class="flex">
                    <object
                        :data="'/storage/services/' + service.id + '/attachments/' + service.attachment"
                        type="application/pdf"
                        frameBorder="0"
                        scrolling="auto"
                        width="100%"
                        class="h-[90vh]"
                    >
                        <div class="flex h-full flex-col items-center justify-center">
                            <div class="rounded-xl bg-gray-100 p-2">
                                <FolderIcon class="h-20 w-20 text-red-500" />
                            </div>

                            <p class="mt-2">Soubor se nepovedlo otevřít.</p>

                            <div class="mt-10 flex justify-center">
                                <div class="slower-animation rounded-md border border-gray-200 bg-white hover:border-red-300">
                                    <a
                                        :aria-label="'Otevřít soubor ' + service.attachment"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        :href="'/storage/services/' + service.id + '/attachments/' + service.attachment"
                                        class="group inline-flex items-center py-2 pl-4 pr-8 text-base font-semibold text-red-400"
                                    >
                                        Otevřít v novém okně

                                        <ArrowRightIcon class="faster-animation relative left-2 h-5 w-5 group-hover:left-4" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </object>
                </div>
            </Modal>
        </template>
    </AppLayout>
</template>
